import { useEffect, useState } from 'react';

import type { QueryParameters, Response as SearchResponse } from 'algoliasearch';
import algoliasearch from 'algoliasearch/lite';

import logger from 'js/app/loggerSingleton';

export type SearchParameters = QueryParameters & {
  hitsPerPage: number;
};

export type Result<T> = T[];

/**
 * Custom hook to fetch data from an Algolia index (currently only articles are indexed).
 *
 * This hook performs a search on the specified Algolia index using the provided search parameters.
 * It returns the search results and the searching state.
 *
 * @param {string} indexName - The name of the Algolia index to search.
 * @param {SearchParameters} searchParameters - The parameters for the Algolia search.
 * @returns {{ results: Result<T>; searching: boolean }} The search results and the searching state.
 */
const useAlgoliaIndexData = <T>(
  indexName: string,
  searchParameters: SearchParameters
): { results: Result<T>; searching: boolean } => {
  const [results, setResults] = useState<Result<T>>([]);
  const [searching, setSearching] = useState(true);

  useEffect(() => {
    const searchClient = algoliasearch('LUA9B20G37', 'dcc55281ffd7ba6f24c3a9b18288499b');
    const index = searchClient.initIndex(indexName);

    index
      .search(searchParameters)
      .then(({ hits }: SearchResponse<T>) => {
        setSearching(false);
        setResults(hits);
      })
      .catch((error) => {
        logger.error(`Failed to index ${indexName}`, error);
        setSearching(false);
        setResults([]);
      });
  }, [indexName, searchParameters]);

  return { results, searching };
};

export default useAlgoliaIndexData;
